import Experience from "@folio-2026/experience"
import {
  billboarding,
  color,
  cos,
  deltaTime,
  dot,
  float,
  floor,
  Fn,
  fract,
  hash,
  If,
  instancedArray,
  instanceIndex,
  length,
  mix,
  normalize,
  normalMap,
  normalWorld,
  positionLocal,
  sin,
  smoothstep,
  texture,
  time,
  uint,
  uniform,
  uv,
  vec2,
  vec3,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

export default class RainWithPuddle {
  experience: Experience
  renderer: THREE.WebGPURenderer
  scene: THREE.Object3D
  camera: THREE.PerspectiveCamera
  debugFolder?: FolderApi

  // Puddle mesh
  puddleMesh?: THREE.Mesh

  // Rain particle system
  computeParticles!: THREE.ComputeNode
  maxParticleCount: number
  instanceCount: number

  positionBuffer: THREE.StorageBufferNode
  velocityBuffer: THREE.StorageBufferNode
  ripplePositionBuffer: THREE.StorageBufferNode
  rippleTimeBuffer: THREE.StorageBufferNode
  tiltAngleBuffer: THREE.StorageBufferNode

  renderOrder = 0

  // Textures
  map!: THREE.Texture
  normal!: THREE.Texture

  // Combined settings
  settings = {
    // Rain settings
    dropHeight: 0.3,
    dropWidth: 0.006,
    dropColor: uniform(color("#384352")),
    intensity: uniform(0.66),
    velocity: uniform(0.9),
    // Puddle settings
    scale: uniform(0.61),
    tileScale: uniform(14),
    normalHeight: uniform(0.25),
    // Brightness controls
    floorBrightness: uniform(1.5),
    // Material settings
    metalness: 0,
    roughness: 1,
    // Ripple settings
    rippleIntensity: uniform(20),
  }

  constructor({
    scene,
    camera,
    renderer,
    map,
    normal,
    maxParticleCount = 2000,
    renderOrder = 0,
    debugFolder,
  }: {
    scene: THREE.Object3D
    camera: THREE.PerspectiveCamera
    renderer: THREE.WebGPURenderer
    map: THREE.Texture
    normal: THREE.Texture
    maxParticleCount?: number
    renderOrder?: number
    debugFolder?: FolderApi
  }) {
    this.experience = Experience.getInstance()
    this.renderer = renderer
    this.scene = scene
    this.camera = camera
    this.debugFolder = debugFolder

    this.renderOrder = renderOrder

    this.maxParticleCount = maxParticleCount
    this.instanceCount = maxParticleCount / 2

    // Setup textures
    this.map = map
    this.map.colorSpace = THREE.SRGBColorSpace
    this.map.wrapS = THREE.RepeatWrapping
    this.map.wrapT = THREE.RepeatWrapping

    this.normal = normal
    this.normal.wrapS = THREE.RepeatWrapping
    this.normal.wrapT = THREE.RepeatWrapping

    // Create GPU buffers for particle data
    this.positionBuffer = instancedArray(this.maxParticleCount, "vec3")
    this.velocityBuffer = instancedArray(this.maxParticleCount, "vec3")
    this.ripplePositionBuffer = instancedArray(this.maxParticleCount, "vec3")
    this.rippleTimeBuffer = instancedArray(this.maxParticleCount, "vec3")
    this.tiltAngleBuffer = instancedArray(this.maxParticleCount, "float")

    this.createMeshes()
    this.setDebug()
    this.init()
  }

  init() {
    // Create a render target for collision detection
    const collisionPosRT = new THREE.RenderTarget(16, 16)
    collisionPosRT.texture.type = THREE.HalfFloatType
    collisionPosRT.texture.magFilter = THREE.NearestFilter
    collisionPosRT.texture.minFilter = THREE.NearestFilter
    collisionPosRT.texture.generateMipmaps = false

    // GPU Compute Shader: Initialize particle positions and velocities
    const randUint = () => uint(Math.random() * 0xffffff)

    const computeInit = Fn(() => {
      const position = this.positionBuffer.element(instanceIndex)
      const velocity = this.velocityBuffer.element(instanceIndex)
      const rippleTime = this.rippleTimeBuffer.element(instanceIndex)
      const tiltAngle = this.tiltAngleBuffer.element(instanceIndex)

      const randX = hash(instanceIndex)
      const randY = hash(instanceIndex.add(randUint()))
      const randZ = hash(instanceIndex.add(randUint()))

      position.x = randX.mul(20).add(-10)
      position.y = randY.mul(25)
      position.z = randZ.mul(15).add(-10)

      velocity.y = randX.mul(-0.9).add(-0.2)

      const tiltAngleValue = hash(instanceIndex.add(randUint()))
        .mul(0.349)
        .sub(0.175)
      tiltAngle.x = tiltAngleValue

      rippleTime.x = 100 as unknown as THREE.Node
    })().compute(this.maxParticleCount)

    // GPU Compute Shader: Update particle physics and handle collisions
    const computeUpdate = Fn(() => {
      const getCoord = (pos: THREE.Node) => pos.add(50).div(100)

      const position = this.positionBuffer.element(instanceIndex)
      const velocity = this.velocityBuffer.element(instanceIndex)
      const ripplePosition = this.ripplePositionBuffer.element(instanceIndex)
      const rippleTime = this.rippleTimeBuffer.element(instanceIndex)

      position.addAssign(velocity.mul(this.settings.velocity))

      const collisionArea = texture(
        collisionPosRT.texture,
        getCoord(position.xz),
      )

      const surfaceOffset = 0.01
      const floorPosition = collisionArea.y.add(surfaceOffset)
      const ripplePivotOffsetY = -this.settings.dropHeight / 2
      // Position ripples slightly above the puddle to avoid z-fighting
      // Puddle is at y=0.001, so place ripples at y=0.003
      const rippleHeightOffset = float(0.003)

      // Only increment ripple time if ripple is active (time < 100 means active)
      const isRippleActive = rippleTime.x.lessThan(float(100))
      If(isRippleActive, () => {
        rippleTime.x = rippleTime.x.add(deltaTime.mul(1.5))
      })

      If(position.y.add(ripplePivotOffsetY).lessThan(floorPosition), () => {
        position.y = 25 as unknown as THREE.Node

        ripplePosition.xz = position.xz
        ripplePosition.y = floorPosition.add(rippleHeightOffset)

        // Reset ripple time to 0 when drop hits (start of animation)
        rippleTime.x = float(0)

        position.x = hash(instanceIndex.add(time)).mul(20).add(-10)
        position.z = hash(instanceIndex.add(time.add(randUint())))
          .mul(15)
          .add(-10)
      })

      // Deactivate ripple if it's been active too long or position is invalid
      const maxRippleTime = float(2.0) // Ripple animation duration
      If(rippleTime.x.greaterThan(maxRippleTime), () => {
        rippleTime.x = float(1000) // Inactive state
      })
    })

    this.computeParticles = computeUpdate().compute(this.maxParticleCount)

    this.renderer.computeAsync(computeInit)
  }

  // Hash functions equivalent to GLSL hash12 and hash22
  private hash12(p: any): any {
    const HASHSCALE1 = 0.1031
    const p3 = fract(vec3(p.x, p.y, p.x).mul(HASHSCALE1))
    const p3Plus = p3.add(dot(p3, vec3(p3.y, p3.z, p3.x).add(19.19)))
    return fract(p3Plus.x.add(p3Plus.y).mul(p3Plus.z))
  }

  private hash22(p: any): any {
    const HASHSCALE3 = vec3(0.1031, 0.103, 0.0973)
    const p3 = fract(vec3(p.x, p.y, p.x).mul(HASHSCALE3))
    const p3Plus = p3.add(dot(p3, vec3(p3.y, p3.z, p3.x).add(19.19)))
    // GLSL: fract((p3.xx+p3.yz)*p3.zy) = vec2(p3.x+p3.x, p3.y+p3.z) * vec2(p3.z, p3.y)
    return fract(
      vec2(p3Plus.x.add(p3Plus.x), p3Plus.y.add(p3Plus.z)).mul(
        vec2(p3Plus.z, p3Plus.y),
      ),
    )
  }

  // Helper function to process one grid cell for ripples
  private processRippleCell(
    p0: any,
    uv: any,
    time: any,
    i: number,
    j: number,
  ): any {
    const MAX_RADIUS = 3
    const pi = p0.add(vec2(float(i), float(j)))
    const hsh = pi
    // Cache hash results to avoid recalculating
    const hash22Result = this.hash22(hsh)
    const p = pi.add(hash22Result)
    const hash12Result = this.hash12(hsh)
    const t = fract(time.mul(0.3).add(hash12Result))
    const v = p.sub(uv)
    const d = length(v).sub(float(MAX_RADIUS + 1).mul(t))
    const h = float(1e-3)
    const d1 = d.sub(h)
    const d2 = d.add(h)
    // Pre-compute smoothstep values to avoid redundant calculations
    const smoothstep1_d1 = smoothstep(-0.6, -0.3, d1)
    const smoothstep2_d1 = smoothstep(0.0, -0.3, d1)
    const smoothstep1_d2 = smoothstep(-0.6, -0.3, d2)
    const smoothstep2_d2 = smoothstep(0.0, -0.3, d2)
    const p1 = sin(d1.mul(31.0)).mul(smoothstep1_d1).mul(smoothstep2_d1)
    const p2 = sin(d2.mul(31.0)).mul(smoothstep1_d2).mul(smoothstep2_d2)
    // Pre-compute common sub-expressions
    const hMul2 = h.mul(2.0)
    const oneMinusT = float(1.0).sub(t)
    const gradient = p2.sub(p1).div(hMul2).mul(oneMinusT.pow(2.0))
    const vNorm = normalize(v)
    return vNorm.mul(gradient).mul(0.5)
  }

  // Procedural ripple system matching MirrorMaterial's getRipples
  // Manually unrolls 7x7 grid (49 cells) to match reference exactly
  private getRipples(uv: any, timeValue: any): any {
    const MAX_RADIUS = 3
    const p0 = floor(uv)
    const time = timeValue.mul(2.0)

    // Manually accumulate all 49 contributions (7x7 grid: -3 to 3 in both dimensions)
    // This must be done explicitly since TSL nodes need to be built at compile time
    const c00 = this.processRippleCell(p0, uv, time, -3, -3)
    const c01 = this.processRippleCell(p0, uv, time, -2, -3)
    const c02 = this.processRippleCell(p0, uv, time, -1, -3)
    const c03 = this.processRippleCell(p0, uv, time, 0, -3)
    const c04 = this.processRippleCell(p0, uv, time, 1, -3)
    const c05 = this.processRippleCell(p0, uv, time, 2, -3)
    const c06 = this.processRippleCell(p0, uv, time, 3, -3)
    const c10 = this.processRippleCell(p0, uv, time, -3, -2)
    const c11 = this.processRippleCell(p0, uv, time, -2, -2)
    const c12 = this.processRippleCell(p0, uv, time, -1, -2)
    const c13 = this.processRippleCell(p0, uv, time, 0, -2)
    const c14 = this.processRippleCell(p0, uv, time, 1, -2)
    const c15 = this.processRippleCell(p0, uv, time, 2, -2)
    const c16 = this.processRippleCell(p0, uv, time, 3, -2)
    const c20 = this.processRippleCell(p0, uv, time, -3, -1)
    const c21 = this.processRippleCell(p0, uv, time, -2, -1)
    const c22 = this.processRippleCell(p0, uv, time, -1, -1)
    const c23 = this.processRippleCell(p0, uv, time, 0, -1)
    const c24 = this.processRippleCell(p0, uv, time, 1, -1)
    const c25 = this.processRippleCell(p0, uv, time, 2, -1)
    const c26 = this.processRippleCell(p0, uv, time, 3, -1)
    const c30 = this.processRippleCell(p0, uv, time, -3, 0)
    const c31 = this.processRippleCell(p0, uv, time, -2, 0)
    const c32 = this.processRippleCell(p0, uv, time, -1, 0)
    const c33 = this.processRippleCell(p0, uv, time, 0, 0)
    const c34 = this.processRippleCell(p0, uv, time, 1, 0)
    const c35 = this.processRippleCell(p0, uv, time, 2, 0)
    const c36 = this.processRippleCell(p0, uv, time, 3, 0)
    const c40 = this.processRippleCell(p0, uv, time, -3, 1)
    const c41 = this.processRippleCell(p0, uv, time, -2, 1)
    const c42 = this.processRippleCell(p0, uv, time, -1, 1)
    const c43 = this.processRippleCell(p0, uv, time, 0, 1)
    const c44 = this.processRippleCell(p0, uv, time, 1, 1)
    const c45 = this.processRippleCell(p0, uv, time, 2, 1)
    const c46 = this.processRippleCell(p0, uv, time, 3, 1)
    const c50 = this.processRippleCell(p0, uv, time, -3, 2)
    const c51 = this.processRippleCell(p0, uv, time, -2, 2)
    const c52 = this.processRippleCell(p0, uv, time, -1, 2)
    const c53 = this.processRippleCell(p0, uv, time, 0, 2)
    const c54 = this.processRippleCell(p0, uv, time, 1, 2)
    const c55 = this.processRippleCell(p0, uv, time, 2, 2)
    const c56 = this.processRippleCell(p0, uv, time, 3, 2)
    const c60 = this.processRippleCell(p0, uv, time, -3, 3)
    const c61 = this.processRippleCell(p0, uv, time, -2, 3)
    const c62 = this.processRippleCell(p0, uv, time, -1, 3)
    const c63 = this.processRippleCell(p0, uv, time, 0, 3)
    const c64 = this.processRippleCell(p0, uv, time, 1, 3)
    const c65 = this.processRippleCell(p0, uv, time, 2, 3)
    const c66 = this.processRippleCell(p0, uv, time, 3, 3)

    // Accumulate all contributions
    const circles = c00
      .add(c01)
      .add(c02)
      .add(c03)
      .add(c04)
      .add(c05)
      .add(c06)
      .add(c10)
      .add(c11)
      .add(c12)
      .add(c13)
      .add(c14)
      .add(c15)
      .add(c16)
      .add(c20)
      .add(c21)
      .add(c22)
      .add(c23)
      .add(c24)
      .add(c25)
      .add(c26)
      .add(c30)
      .add(c31)
      .add(c32)
      .add(c33)
      .add(c34)
      .add(c35)
      .add(c36)
      .add(c40)
      .add(c41)
      .add(c42)
      .add(c43)
      .add(c44)
      .add(c45)
      .add(c46)
      .add(c50)
      .add(c51)
      .add(c52)
      .add(c53)
      .add(c54)
      .add(c55)
      .add(c56)
      .add(c60)
      .add(c61)
      .add(c62)
      .add(c63)
      .add(c64)
      .add(c65)
      .add(c66)

    // Average contributions (divide by total cells: 7*7 = 49)
    const totalCells = float((MAX_RADIUS * 2 + 1) * (MAX_RADIUS * 2 + 1))
    const averaged = circles.div(totalCells)

    // Return as 3D normal vector (z = 0 for flat surface)
    return vec3(averaged.x, averaged.y, float(0.0))
  }

  createMeshes() {
    // Create puddle ground mesh with procedural ripple normals
    const uvValue = uv()
    // Cache perlin texture reference (used multiple times)
    const perlinTexture = this.experience.globalResources
      .perlin as THREE.Texture

    const puddleGeometry = new THREE.PlaneGeometry(30, 23)
    const puddleMaterial = new THREE.MeshPhysicalNodeMaterial()
    puddleMaterial.metalness = this.settings.metalness
    puddleMaterial.roughness = this.settings.roughness

    // Puddle mask using perlin noise (similar to reference component)
    const puddleTexture = texture(
      perlinTexture,
      uvValue.mul(this.settings.scale),
    )
      .pow(4)
      .clamp()

    const colorMap = texture(this.map, uvValue.mul(this.settings.tileScale))

    // World position for ripple calculations (matching MirrorMaterial)
    // Use positionLocal - plane is rotated -90 on X, so local Y becomes world -Z
    // MirrorMaterial uses: worldUV = vPosition.xz; worldUV.x += 0.5; worldUV.y += 0.5;
    // Then: getRipples(worldUV * 8.0)
    // For rotated plane: positionLocal.x → world X, positionLocal.y → world -Z
    // So we use x and -y (or just y if the coordinate system works out)
    const worldPosXZ = vec2(positionLocal.x, positionLocal.y).add(0.5).mul(8.0)

    // Calculate procedural ripples using grid-based system (matching MirrorMaterial)
    const timeValue = time
    const rippleNormals = this.getRipples(worldPosXZ, timeValue)

    // Base normal from texture
    const normalTexture = texture(
      this.normal,
      uvValue.mul(this.settings.tileScale),
    )
    const baseNormal = normalMap(normalTexture, this.settings.normalHeight)

    // Perturb normal with ripples (matching reference's perturbNormal with strength 0.75)
    const surfaceNormal = normalize(normalWorld)
    // Increase strength significantly to make ripples visible from all camera angles
    const rippleStrength = float(8.0) // Increased from 2.0 to make ripples visible from all angles
    const rippleDot = dot(rippleNormals, surfaceNormal)
    const rippleNormalOrthogonal = rippleNormals.sub(
      surfaceNormal.mul(rippleDot),
    )
    let perturbedNormal = normalize(
      surfaceNormal.sub(rippleNormalOrthogonal.mul(rippleStrength)),
    )

    // Add wind noise to normals (matching MirrorMaterial lines 239-247)
    // Use positionLocal for local plane position (vPosition equivalent)
    // Plane is rotated -90 on X, so positionLocal gives us local position
    // Convert to 3D for noise sampling: use positionLocal, add time offset to Y component
    const localPos = vec3(positionLocal.x, positionLocal.y, positionLocal.z)
    const noiseNormalPosition = localPos.mul(40.0)
    const noiseYWithTime = noiseNormalPosition.y.add(timeValue.mul(5.0))
    // Sample perlin at different offsets to simulate FBM
    // Optimize: cache base X coordinate to reduce redundant additions
    const noiseBaseX = noiseNormalPosition.x
    const windNoiseX = texture(
      perlinTexture,
      vec2(noiseBaseX, noiseYWithTime),
    ).r
    const windNoiseY = texture(
      perlinTexture,
      vec2(noiseBaseX.add(0.5), noiseYWithTime.add(0.5)),
    ).r
    const windNoiseZ = texture(
      perlinTexture,
      vec2(noiseBaseX.add(1.0), noiseYWithTime.add(1.0)),
    ).r
    const windNormal = vec3(windNoiseX, windNoiseY, windNoiseZ)
    const windDot = dot(windNormal, surfaceNormal)
    const windNormalOrthogonal = windNormal.sub(surfaceNormal.mul(windDot))
    perturbedNormal = normalize(
      perturbedNormal.sub(windNormalOrthogonal.mul(0.05)),
    ) // Wind strength 0.05

    // Puddle mask for blending (matching MirrorMaterial smoothstep values)
    const puddleNormalMask = smoothstep(0.6, 1.0, puddleTexture.r) // Line 233

    // Blend normals: use perturbed normal in puddle areas (matching line 233)
    // Always use perturbed normal in puddle areas to make ripples visible from all angles
    const finalNormal = mix(baseNormal, perturbedNormal, puddleNormalMask)
    puddleMaterial.normalNode = finalNormal

    const rippleIntensity = length(rippleNormals.xy)
      .mul(this.settings.rippleIntensity)
      .mul(puddleTexture.r)

    puddleMaterial.roughnessNode = rippleIntensity

    puddleMaterial.colorNode = colorMap.mul(this.settings.floorBrightness)

    const puddleMesh = new THREE.Mesh(puddleGeometry, puddleMaterial)
    this.puddleMesh = puddleMesh

    puddleMesh.position.y = 0.001
    puddleMesh.position.z = 0
    puddleMesh.rotation.x = -Math.PI / 2

    // puddleMesh.add(groundReflector.target)
    this.scene.add(puddleMesh)

    // Create raindrop visual material
    const rainMaterial = new THREE.MeshBasicNodeMaterial()
    // Reuse uvValue instead of creating new uv() call
    const rainUvValue = uvValue
    const gradient = length(vec2(rainUvValue.x, rainUvValue.y.sub(0.5)))
      .oneMinus()
      .pow(2)
      .mul(2)
      .mul(this.settings.intensity)

    rainMaterial.colorNode = gradient.mul(this.settings.dropColor)

    rainMaterial.opacityNode = Fn(() => {
      const worldPos = this.positionBuffer.toAttribute()

      const distToLeft = worldPos.x.sub(-10)
      const distToRight = float(10).sub(worldPos.x)
      const distToBack = worldPos.z.sub(-10)
      const distToFront = float(5).sub(worldPos.z)

      const minDist = distToLeft
        .min(distToRight)
        .min(distToBack)
        .min(distToFront)

      return smoothstep(0, 10, minDist)
    })()

    const tiltAngle = this.tiltAngleBuffer.toAttribute()
    rainMaterial.positionNode = Fn(() => {
      const localPos = positionLocal

      const cosTilt = cos(tiltAngle)
      const sinTilt = sin(tiltAngle)
      return vec3(
        localPos.x.mul(cosTilt).sub(localPos.y.mul(sinTilt)),
        localPos.x.mul(sinTilt).add(localPos.y.mul(cosTilt)),
        localPos.z,
      )
    })()

    rainMaterial.vertexNode = billboarding({
      position: this.positionBuffer.toAttribute(),
    })
    rainMaterial.transparent = true
    rainMaterial.opacity = 0.2
    rainMaterial.side = THREE.DoubleSide
    rainMaterial.forceSinglePass = false
    rainMaterial.depthWrite = false
    rainMaterial.depthTest = true

    const rainParticles = new THREE.Mesh(
      new THREE.PlaneGeometry(
        this.settings.dropWidth,
        this.settings.dropHeight,
      ),
      rainMaterial,
    )
    rainParticles.frustumCulled = false
    rainParticles.count = this.instanceCount
    this.scene.add(rainParticles)

    // Note: Ripples are now applied directly to the puddle material as normal perturbations
    // No separate ripple meshes needed - they're part of the puddle shader
  }

  update() {
    this.renderer.compute(this.computeParticles)
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "🌧️ Rain with Puddle",
      expanded: false,
    })

    // Rain settings
    const rainSettings = {
      dropColor: `#${new THREE.Color(this.settings.dropColor.value).getHexString()}`,
      intensity: this.settings.intensity.value,
      velocity: this.settings.velocity.value,
    }

    folder
      .addBinding(rainSettings, "dropColor", {
        view: "color",
      })
      .on("change", (ev) => {
        this.settings.dropColor.value = new THREE.Color(ev.value)
      })

    folder
      .addBinding(rainSettings, "intensity", {
        min: 0,
        max: 1,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.intensity.value = ev.value
      })

    folder
      .addBinding(rainSettings, "velocity", {
        min: 0,
        max: 2,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.velocity.value = ev.value
      })

    // Puddle settings
    const puddleSettings = {
      scale: this.settings.scale.value,
      tileScale: this.settings.tileScale.value,
      normalHeight: this.settings.normalHeight.value,
      floorBrightness: this.settings.floorBrightness.value,
      metalness: this.settings.metalness,
      roughness: this.settings.roughness,
      rippleIntensity: this.settings.rippleIntensity.value,
    }

    folder
      .addBinding(puddleSettings, "scale", {
        min: 0,
        max: 1,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.scale.value = ev.value
      })

    folder
      .addBinding(puddleSettings, "tileScale", {
        label: "Color tile scale",
        min: 5,
        max: 20,
        step: 1,
      })
      .on("change", (ev) => {
        this.settings.tileScale.value = ev.value
      })

    folder
      .addBinding(puddleSettings, "normalHeight", {
        min: 0,
        max: 10,
        step: 0.1,
      })
      .on("change", (ev) => {
        this.settings.normalHeight.value = ev.value
      })

    folder
      .addBinding(puddleSettings, "floorBrightness", {
        label: "Floor brightness",
        min: 0,
        max: 2,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.floorBrightness.value = ev.value
      })

    folder
      .addBinding(puddleSettings, "metalness", {
        label: "puddleMaterial Metalness",
        min: 0,
        max: 1,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.metalness = ev.value
        if (this.puddleMesh) {
          const material = this.puddleMesh
            .material as THREE.MeshPhysicalNodeMaterial
          material.metalness = ev.value
        }
      })

    folder
      .addBinding(puddleSettings, "roughness", {
        label: "puddleMaterial Roughness",
        min: 0,
        max: 1,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.roughness = ev.value
        if (this.puddleMesh) {
          const material = this.puddleMesh
            .material as THREE.MeshPhysicalNodeMaterial
          material.roughness = ev.value
        }
      })

    folder
      .addBinding(puddleSettings, "rippleIntensity", {
        label: "Ripple intensity",
        min: 0,
        max: 30,
        step: 0.1,
      })
      .on("change", (ev) => {
        this.settings.rippleIntensity.value = ev.value
      })
  }
}
